import ApiKeyService, { UserTypes } from '@/services/ApiKeyService'
import { AuthUser, Company, CustomerType, Location as LocationModel, User } from '../models'
import { CUSTOM_AGENCIES_PERMISSIONS, SUB_ACCOUNT_RESTRICTED_STATUS } from '../apps/saas/constants'
import {
  DashboardRouteNames,
  ProductRouteNames,
  conversationRouteNames,
  launchPadRouteNames,
  locationSettingsRouteNames,
  ordersRouteNames,
  paymentSettingsRouteNames,
  restrictedLocationRouteNames,
  subscriptionsRouteNames,
  suspendedLocationRouteNames,
  transactionsRouteNames,
} from '@/routes/constants/location'
import { ILocationSettingConfig, LOCATION_SETTING_CONFIG } from '../util/location_setting_config'
import { Location, RawLocation, Route } from 'vue-router'
import { NamedPromise, executeCriticalPromises, executeDegradablePromises } from '../util/interaction-tracker/promiseExecutor'
import { agencyDashboardRoutes, subAccountRoutes } from '@/routes/constants/agency/'
import { createNavigationContext, getFallbackRoute } from '@/util/navigationFallback'
import { locationLevelRoutesPermissions, paymentBetaAccessRoutes, routesRequiredPermissions } from '@/routes/permissions'
import { restrictUserByAgencyPermission, restrictUserByScopePermission } from '../util/helper'
import { shouldHideLaunchpad, userRoutesPermissionChecker } from '@/pmd/composition/userPermission'

import FunnelsServices from '../services/FunnelServices'
import LocationServices from '../services/LocationServices'
import { agencyLaunchpadRoutes } from '@/routes/constants/agency/'
import { awaitToJS } from '@/util/await-to-js'
import { checkAndRedirectToSummerOfAI } from '@/util/helper'
import cloneDeep from 'lodash/cloneDeep'
import first from 'lodash-es/first'
import isEmpty from 'lodash-es/isEmpty'
import keys from 'lodash-es/keys'
import { locationNavigation } from '@/util/navigation'
import moment from 'moment'
import { onboardingRoutes } from './constants/onboarding'
import { paymentSetupRouteNames } from './constants/location/paymentSetup'
import { reputationRouteNames } from './constants/location'
import { settingsRoutes } from './constants/agency/settings'
import { setupLocale } from '../bootstrap'
import store from '../store'

const { checkFormsPermissions, checkQuizzesPermissions, checkSurveysPermissions, checkQrCodesPermissions, checkLaunchpadPermissions } =
  userRoutesPermissionChecker()

let firstLoad = true

export const inactiveAuthAgency = async (to: Route, _: Route, next: (to?: Location) => void) => {
  const company: Company = await store.dispatch('company/get')
  const currentUser = await store.dispatch('user/get')
  const user = new User(cloneDeep(currentUser))

  const inactiveLockedRoutes = ['suspended-agency', 'sandbox-inactive', 'inactive', 'inactive_location']

  const skipLockout = () => {
    const isLockoutSkipped = store.getters['supportPreferences/isLockoutSkipped']
    return isLockoutSkipped
  }

  const redirectTo = (goTo: Location) => (to.name === goTo.name ? next() : next(goTo))

  const allowLocation = () => {
    if (inactiveLockedRoutes.includes(to.name as string)) redirectTo({ name: 'dashboard', replace: true })
  }

  const allowAgency = () => {
    if (inactiveLockedRoutes.includes(to.name as string)) redirectTo({ name: 'agency_dashboard', replace: true })
  }

  const allowAll = () => {
    if (user.type === User.TYPE_ACCOUNT) return allowLocation()
    else return allowAgency()
  }

  // auth checks are in precedence order, make sure to check precedence before adding new case

  // 1. suspended account
  if (company.activeStatusDetails?.is_temporarily_suspended) {
    if (skipLockout()) return allowAll()
    return redirectTo({ name: 'suspended-agency', replace: true })
  }

  // 2. sandbox account
  else if (company.isSandboxAccount) {
    if (skipLockout()) return allowAll()
    try {
      const timeLeftForInactivation = company.dateInactivation.valueOf() - +new Date()
      const isLocked = timeLeftForInactivation < 0 || company.status.includes('inactive')
      if (isLocked) {
        return redirectTo({
          name: 'sandbox-inactive',
          replace: true,
        })
      } else {
        return allowAgency()
      }
    } catch {
      // console.error(error)
    }
  }

  // 3. payment overdue
  else if (company.status === 'active_failed-payment') {
    if (skipLockout()) return allowAll()
    if (user.type === User.TYPE_ACCOUNT) {
      // allow location users to access the app
      return allowLocation()
    } else if (user.role === User.ROLE_USER) {
      // allow agency users to access the app
      return allowAgency()
    } else {
      // redirect agency admin/owner to inactive screen if lockout is not skipped and time to inactivation is less than 5 days
      const timeLeftForInactivation = company.dateInactivation.valueOf() - +new Date()
      const lockoutSkippedTimestamp = localStorage.getItem('lockoutSkipped')
      // lockout skipped is valid for 24 hours
      const lockoutSkipped = lockoutSkippedTimestamp && parseInt(lockoutSkippedTimestamp) + 24 * 60 * 60 * 1000 > +new Date()
      if (!lockoutSkipped && timeLeftForInactivation < 5 * 24 * 60 * 60 * 1000) {
        if (to.name === 'inactive' && !to.query.type)
          return next({
            name: 'inactive',
            replace: true,
            query: { type: 'failed-payment' },
          })
        return redirectTo({
          name: 'inactive',
          replace: true,
          query: { type: 'failed-payment' },
        })
      } else {
        // lockout skipped allow to access the app
        return allowAgency()
      }
    }
  }

  // 4. default inactive check
  // TODO: use activeStatusDetails to check for inactive status after company api refactor
  else if (company.status.startsWith('inactive')) {
    if (skipLockout()) return allowAll()
    // block all user access, redirect them to inactive screen
    if (to.name === 'inactive' && !!to.query.type) return next({ name: 'inactive', replace: true, query: undefined })
    return redirectTo(user.type === User.TYPE_ACCOUNT ? { name: 'inactive_location', replace: true } : { name: 'inactive', replace: true })
  }

  // 5. default active check
  else {
    // allow all user access, redirect them to dashboard if they try to access inactive screen
    return allowAll()
  }
}

const requireAgencyAdminProductEnabled = async (to: Route, from: Route, next: (to?: RawLocation) => void, user: User) => {
  try {
    const isAgencyProductEnabled = await store.dispatch('locationSetting/fetchAllProductOptInStatus')
    const configEntry = Object.entries(LOCATION_SETTING_CONFIG).find(
      ([, config]: [string, ILocationSettingConfig[string]]) => config.routerName === to.name
    )
    const productKey = configEntry ? configEntry[0] : undefined
    if (productKey && (!isAgencyProductEnabled[productKey] || user?.role !== 'admin' || user?.type !== 'agency')) {
      if (productKey === 'conversationAI')
        //return next()
        return next({ path: '/', replace: true })
    }
    return
  } catch (error) {
    console.error(error)
    return next({ path: '/', replace: true })
  }
}

export const requireAuth = async (to: Route, from: Route, next: (to?: RawLocation) => void) => {
  try {
    const currentUser = await store.dispatch('user/get')
    const user = new User(cloneDeep(currentUser))
    await inactiveAuthAgency(to, from, next)
    await requireAgencyAdminProductEnabled(to, from, next, user)

    const company = await store.dispatch('company/get')
    await store.dispatch('featureFlags/getFeatureFlags', {
      companyId: company.id,
    })
    const is497Plan = company && company.stripeActivePlan && company.stripeActivePlan.includes('497')

    if (
      company &&
      company.onboardingInfo.pending === true &&
      company.status !== 'active_test-agency' &&
      user &&
      user.type === 'agency' &&
      to.name !== 'onboarding-trial-call' &&
      to.name !== onboardingRoutes.onboardingFlow.version_a
      // TODO: Need to check why this condition is needed in vue-router 4
    ) {
      return next({
        name: onboardingRoutes.onboardingFlow.version_a,
        replace: true,
      })
    }

    if (user.type !== User.TYPE_AGENCY && !(to.name === 'inactive_location' || to.name === 'inactive' || to.name === 'shopify_dashboard')) {
      return next({ name: 'dashboard', replace: true })
    } else {
      // for all agency users.
      if (to.name === agencyDashboardRoutes.default) {
        if (
          company.saasSettings && company.saasSettings.agency_dashboard_visible_to === 'individual'
            ? user?._data?.is_agency_owner || user?._data?.isAgencyOwner
            : user.role === User.ROLE_ADMIN
        ) {
          return next()
        } else {
          return next({ name: subAccountRoutes.accounts.default, replace: true })
        }
      } else if (to.name === settingsRoutes.stripe) {
        if (user.type === User.TYPE_AGENCY && user.role === User.ROLE_ADMIN) {
          return next()
        } else {
          return next({ name: agencyDashboardRoutes.default, replace: true })
        }
      } else if (to.name === subAccountRoutes.accounts.default && is497Plan) {
        return next({ name: subAccountRoutes.default, replace: true })
      } else if (to.name === subAccountRoutes.default && !is497Plan) {
        return next({ name: subAccountRoutes.accounts.default, replace: true })
      } else if (to.name === subAccountRoutes.dashboard && !is497Plan) {
        return next({ name: subAccountRoutes.accounts.default, replace: true })
      } else if (to.name === subAccountRoutes.schedules && !is497Plan) {
        return next({ name: subAccountRoutes.accounts.default, replace: true })
      } else if (to.name === agencyLaunchpadRoutes.default && !user.scopes.includes('agency/launchpad.write')) {
        return next({ name: agencyDashboardRoutes.main, replace: true })
      } else {
        return next()
      }
    }
  } catch {
    return next({
      name: 'login',
      query: { url: encodeURIComponent(to.fullPath) },
      replace: true,
    })
  }
}

export const requireAuthAgencyAdmin = async (to: Route, from: Route, next) => {
  try {
    const currentUser = await store.dispatch('user/get')
    const user = new User(cloneDeep(currentUser))
    if (user.role === 'admin') {
      await requireAuth(to, from, next)
    } else {
      next({ path: '/', replace: true })
    }
  } catch {
    next({ path: '/', replace: true })
  }
}

export const requireAuthAgencyAdminWithApiKeys = async (to: Route, from: Route, next) => {
  try {
    const currentUser = await store.dispatch('user/get')
    const user = new User(cloneDeep(currentUser))

    if (user.role !== 'admin' || user.type !== User.TYPE_AGENCY) {
      next({ path: '/', replace: true })
      return
    }

    // Check if agency has API keys
    const company = await store.dispatch('company/get')
    try {
      const response: any = await ApiKeyService.getKeys(company.id, UserTypes.Company)
      if (response?.keys?.length > 0) {
        await requireAuth(to, from, next)
      } else {
        // No API keys, redirect to settings
        next({ path: '/settings/company', replace: true })
      }
    } catch (err) {
      console.error('Error fetching agency API keys:', err)
      // On error, allow access
      await requireAuth(to, from, next)
    }
  } catch {
    next({ path: '/', replace: true })
  }
}

const forceLockedTimestamp = 1607990400 // 12/15/2020 @ 12:00am (UTC)

export const inactiveAuthLocation = async (to: Route, from: Route, next: (to?: RawLocation) => void) => {
  const company = await store.dispatch('company/get')
  const currentUser = await store.dispatch('user/get')
  const user = new User(cloneDeep(currentUser))
  const lockoutSkippedTimestamp = localStorage.getItem('lockoutSkipped')

  const lockoutSkipped = lockoutSkippedTimestamp ? parseInt(lockoutSkippedTimestamp) + 24 * 60 * 60 * 1000 > +new Date() : false

  // 1. check if location-level user
  if (user.type === User.TYPE_ACCOUNT) {
    if (company.status && company.status.includes('inactive') && +new Date() > forceLockedTimestamp * 1000) {
      if (to.name === 'inactive_location') {
        // to prevent infinite loop
        next()
      } else {
        next({ name: 'inactive_location', replace: true })
      }
    } else {
      // continue
      return
    }
  } else if (!lockoutSkipped) {
    // 2. Agency Level user
    // 2.1 Lock-out with `active_failed-payment` case
    if (company.status && company.status === 'active_failed-payment' && company._data && company._data.date_inactivation) {
      // 2.1.1 Date of inactivation must be within next 5 days.
      const msLeftForInactivation = company._data.date_inactivation.seconds * 1000 - +new Date()
      if (msLeftForInactivation > 0 && msLeftForInactivation < (7 - 5) * 24 * 60 * 60 * 1000) {
        // 5 days
        if (to.name === 'inactive' && to.query.type === 'failed-payment') {
          // to prevent infinite loop
          next()
        } else {
          next({
            name: 'inactive',
            replace: true,
            query: { type: 'failed-payment' },
          })
        }
      }
    }
    // 2.2 Lock-out with all other `inactive` cases
    if (company.status && company.status.includes('inactive')) {
      if (to.name === 'inactive') {
        // to prevent infinite loop
        next()
      } else {
        next({ name: 'inactive', replace: true })
      }
    } else {
      // continue
      return
    }
  }
  return
}

export const redirectBusinessRoute = (to: Route, from: Route, next: (to?: RawLocation) => void) => {
  const isNewCompaniesPage = store.getters['featureFlags/getForLocation']('customObjects.newCompaniesPage')
  const user = store.getters['user/get']
  const userHasWriteSchemaPerms = user.role === 'admin' ? true : false

  if (isNewCompaniesPage && userHasWriteSchemaPerms) {
    const listOfStandardSchemas = store.getters['customObjectsStore/getStandardObjectSchemas']
    const businessCustomObject = listOfStandardSchemas.find(obj => obj.key === 'business')

    return next({
      name: 'custom-object-details',
      params: { ...(to.params || {}), customObjectId: businessCustomObject?.id },
      query: to.query,
      replace: true,
    })
  }

  return requireAuthAndLocation(to, from, next)
}

export const requireAuthAndLocation = (to: Route, from: Route, next: (to?: RawLocation) => void) => {
  // console.log('requireAuthAndLocation')
  store
    .dispatch('auth/get')
    .then(async (auth: AuthUser) => {
      // console.log('auth', auth)
      const currentUser = await store.dispatch('user/get')
      const user = new User(cloneDeep(currentUser))
      const saasPermissions = new LocationModel(await store.state.locations.currentLocation)
      const permissions = user.permissions
      await inactiveAuthLocation(to, from, next)
      const userPermissions = store.getters['user/getScopes']
      const requiredPermission = routesRequiredPermissions[to.name]

      if (to.name === 'wordpress-v2') {
        await store.dispatch('wordpress/fetchOfferingEnabled', to.params.location_id)
        const offeringEnabled = store.getters['wordpress/isWordPressOfferingEnabled']
        if (offeringEnabled.hasFetchedOfferingEnabled && !offeringEnabled.isOfferingEnabled) {
          return next({
            name: DashboardRouteNames.default,
            params: to.params,
            query: to.query,
            replace: true,
          })
        }

        if (!userPermissions.includes('wordpress.write') && !userPermissions.includes('wordpress.read')) {
          return next({
            name: DashboardRouteNames.default,
            params: to.params,
            query: to.query,
            replace: true,
          })
        }
      }

      if (to.name === 'form-builder-main-v2') {
        if (!checkFormsPermissions(saasPermissions.permissions, store.getters['user/getScopes'])) {
          return next({
            name: DashboardRouteNames.default,
            params: to.params,
            query: to.query,
            replace: true,
          })
        }
      }

      if (to.name === 'quiz-builder-main') {
        if (!checkQuizzesPermissions(saasPermissions.permissions, store.getters['user/getScopes'])) {
          return next({
            name: DashboardRouteNames.default,
            params: to.params,
            query: to.query,
            replace: true,
          })
        }
      }
      if (to.name === 'survey-builder-main-v2') {
        if (!checkSurveysPermissions(saasPermissions.permissions, store.getters['user/getScopes'])) {
          return next({
            name: DashboardRouteNames.default,
            params: to.params,
            query: to.query,
            replace: true,
          })
        }
      }
      if (to.name === 'qr-codes') {
        if (!checkQrCodesPermissions(saasPermissions.permissions, store.getters['user/getScopes'])) {
          return next({
            name: DashboardRouteNames.default,
            params: to.params,
            query: to.query,
            replace: true,
          })
        }
      }

      // Payment route permission mappings
      const PAYMENT_SETTINGS_ROUTE_PERMISSIONS = {
        [paymentSettingsRouteNames.settingsReceipts]: {
          permission: 'payments/settings.configureReceipt',
          fallbackRoute: paymentSettingsRouteNames.settingsTaxes,
        },
        [paymentSettingsRouteNames.settingsTaxes]: {
          permission: 'payments/taxesSettings.readonly',
          fallbackRoute: paymentSettingsRouteNames.settingsNotifications,
        },
        [paymentSettingsRouteNames.settingsSubscriptions]: {
          permission: 'payments/settings.configureSubscription',
          fallbackRoute: paymentSettingsRouteNames.settingsMiscellaneousCharges,
        },
      }

      const PAYMENT_PAGES_ROUTE_PERMISSIONS = {
        [ordersRouteNames.orders]: 'payments/orders.readonly',
        [subscriptionsRouteNames.subscriptions]: 'payments/subscriptions.readonly',
        [transactionsRouteNames.transactions]: 'payments/transactions.readonly',
        [transactionsRouteNames.transactionDetails]: 'payments/transactions.readonly',
        [ProductRouteNames.products]: 'products.readonly',
        [paymentSettingsRouteNames.settings]: 'payments/settings.readonly',
      }

      // Check payment settings routes (with specific fallback routes)
      for (const [routeName, config] of Object.entries(PAYMENT_SETTINGS_ROUTE_PERMISSIONS)) {
        if (to.name === routeName && !userPermissions.includes(config.permission)) {
          return next({
            name: config.fallbackRoute,
            params: to.params,
            query: to.query,
            replace: true,
          })
        }
      }

      // Check payment pages routes (fallback to dashboard)
      for (const [routeName, permission] of Object.entries(PAYMENT_PAGES_ROUTE_PERMISSIONS)) {
        if (to.name === routeName && !userPermissions.includes(permission)) {
          return next({
            name: DashboardRouteNames.default,
            params: to.params,
            query: to.query,
            replace: true,
          })
        }
      }

      if (to.name == 'location-v2') {
        return next({
          name: DashboardRouteNames.default,
          params: to.params,
          query: to.query,
          replace: true,
        })
      }

      if (to.name === 'scoring') {
        if (!userPermissions.includes('settings.write')) {
          return next({
            name: DashboardRouteNames.default,
            params: to.params,
            query: to.query,
            replace: true,
          })
        }

        if (user.type === User.TYPE_ACCOUNT && user.role !== User.ROLE_ADMIN) {
          return next({
            name: DashboardRouteNames.default,
            params: to.params,
            query: to.query,
            replace: true,
          })
        }
      }

      if (to.name === launchPadRouteNames.default) {
        const hideLaunchpad = shouldHideLaunchpad(
          store?.state?.company?.company?.hide_launchpad,
          store?.getters?.['featureFlags/getForLocation']?.('location-launchpad-v2')
        )
        if (!checkLaunchpadPermissions(saasPermissions.permissions, store.getters['user/getScopes'], hideLaunchpad)) {
          const context = createNavigationContext(store, to)
          const fallback = getFallbackRoute(launchPadRouteNames.default, locationNavigation, context)
          return next({
            name: fallback.routeName,
            params: fallback.params,
            query: fallback.query,
            replace: fallback.replace,
          })
        }
      }

      if (to.name === 'audit-report' && to?.params?.location_id?.length) {
        const userPermissions = store.getters['user/getScopes']
        let auditReportUserPermission = false
        if (['prospecting/auditReport.write'].some(permission => userPermissions.includes(permission))) {
          auditReportUserPermission = true
        }
        if (!auditReportUserPermission) {
          return next({
            name: launchPadRouteNames.default,
            params: to.params,
            query: to.query,
            replace: true,
          })
        }
      }

      if (to.name === reputationRouteNames.gbp) {
        // if GBP permission is not enabled, redirect to dashboard
        const isGbpPermissionEnabled = store.getters['user/getScopes'].includes('reputation/gbp.write')
        if (!isGbpPermissionEnabled) {
          return next({
            name: DashboardRouteNames.default,
            params: to.params,
            query: to.query,
            replace: true,
          })
        }
      }

      if (to.name === reputationRouteNames.videoTestimonials) {
        const videoTestimonialsLabStatus = store.getters['featureFlags/getForLocation']('reputation.videoTestimonials')
        if (!videoTestimonialsLabStatus) {
          return next({
            name: DashboardRouteNames.default,
            params: to.params,
            query: to.query,
            replace: true,
          })
        }
      }

      const handleRoutePermission = () => {
        if (to.name == DashboardRouteNames.default) {
          return next({
            name: conversationRouteNames.conversationsIdV2,
            params: to.params,
            query: to.query,
            replace: true,
          })
        } else if (to.name !== conversationRouteNames.conversationsIdV2) {
          return next({
            name: DashboardRouteNames.default,
            params: to.params,
            query: to.query,
            replace: true,
          })
        } else {
          return next({
            path: from.path,
            replace: true,
          })
        }
      }

      /**
       * To support any-of for the routes. Ex: If I have workflows_enabled or workflows_read_only, I want to let the user go to the specific routes.
       * This is to support Arrays in routes/permissions.ts/routesRequiredPermissions
       */
      if (Array.isArray(requiredPermission)) {
        const isAnyOfThemEnabled = requiredPermission.find(p => permissions[p] === true)
        if (!isAnyOfThemEnabled) {
          handleRoutePermission()
        }
      } else if (permissions[requiredPermission] === false) {
        handleRoutePermission()
      }

      const company = await store.dispatch('company/get')

      if (requiredPermission === 'reporting_enabled') {
        const companyPlan = parseInt(company.stripeActivePlan?.split('_')[2])
        const userAllowedForReports =
          (companyPlan >= 297 || store.getters['LevelUpDayFlag/isFeaturesActive2025']) &&
          store.state.user.scopes.includes('reporting/reports.readonly')
        if (!userAllowedForReports) {
          return next({
            name: 'dashboard-v2',
            params: to.params,
            query: to.query,
            replace: true,
          })
        }
      }

      //

      const sidebarVersion = store.getters['sidebarv2/getVersion']

      if (
        company &&
        company.onboardingInfo.pending === true &&
        company.status !== 'active_test-agency' &&
        user &&
        user.type === 'agency' &&
        to.name !== 'onboarding-trial-call'
      ) {
        return next({
          name: onboardingRoutes.onboardingFlow.version_a,
          replace: true,
        })
      }
      if (!to.params.location_id) {
        if (company.customerType === CustomerType.AGENCY && user.type === User.TYPE_AGENCY) {
          try {
            if (user.role === User.ROLE_ADMIN) {
              const redirectedToSummerOfAIMenu = await checkAndRedirectToSummerOfAI({
                store,
                router: null,
                next,
                locationId: null,
              })
              if (redirectedToSummerOfAIMenu) return
            }
          } catch (err) {
            console.error('redirect to summer of ai failed: ', err)
          }
          if (
            company.saasSettings && company.saasSettings.agency_dashboard_visible_to === 'individual'
              ? user?._data?.isAgencyOwner || user?._data?.is_agency_owner
              : user.role === User.ROLE_ADMIN
          ) {
            const redirectToAgencyLaunchpad = await store.dispatch('launchpad/redirectToAgencyLaunchpad')
            if (redirectToAgencyLaunchpad) {
              return next({ name: agencyLaunchpadRoutes.default, replace: true })
            } else {
              return next({ name: agencyDashboardRoutes.main, replace: true })
            }
          } else {
            return next({ name: subAccountRoutes.accounts.default, replace: true })
          }
        } else if (!isEmpty(user.locations)) {
          // This is an location level user who only has access to certain locations.
          const locationId = first(keys(user.locations || {}).sort()) || ''
          const location = await store.dispatch('locations/getById', locationId)
          if (
            location.suspended_info &&
            location.suspended_info.restricted &&
            to.name !== restrictedLocationRouteNames.default &&
            SUB_ACCOUNT_RESTRICTED_STATUS.includes(location.suspended_info.approval_state) &&
            user.type === User.TYPE_ACCOUNT
          ) {
            return next({
              name: restrictedLocationRouteNames.default,
              replace: true,
              params: { location_id: locationId },
            })
          }
          if (
            location?.suspended_info &&
            location?.suspended_info?.paused &&
            to.name !== suspendedLocationRouteNames.default &&
            user.type === User.TYPE_ACCOUNT
          ) {
            return next({
              name: suspendedLocationRouteNames.default,
              replace: true,
              params: { location_id: locationId },
            })
          }
          if (
            location?.suspended_info &&
            location?.suspended_info?.payment_pending &&
            to.name !== paymentSetupRouteNames.default &&
            user.type === User.TYPE_ACCOUNT
          ) {
            return next({
              name: paymentSetupRouteNames.default,
              replace: true,
              params: { location_id: locationId },
            })
          }
          if (
            location?.active_status_details &&
            location?.active_status_details?.is_temporarily_suspended &&
            to.name !== suspendedLocationRouteNames.default &&
            user.type === User.TYPE_ACCOUNT
          ) {
            return next({
              name: suspendedLocationRouteNames.default,
              replace: true,
              params: { location_id: locationId },
            })
          }
          if (sidebarVersion != 'v2') {
            return next({
              name: 'dashboard',
              replace: true,
              params: { location_id: locationId },
            })
          } else {
            return next({
              name: DashboardRouteNames.default,
              replace: true,
              params: { location_id: locationId },
            })
          }
        } else {
          // Direct accounts will come into this case.
          const locations = await LocationModel.fetchAllLocations()
          if (locations.length > 0) {
            return next({
              name: DashboardRouteNames.default,
              replace: true,
              params: { location_id: locations[0].id },
            })
          }
        }
        return
      } else {
        if (user.type === User.TYPE_ACCOUNT) {
          const availableLocations = keys(user.locations) || []
          if (availableLocations.indexOf(to.params.location_id) === -1) {
            return next({
              path: '/',
              replace: true,
            })
          }
        }
        const location = await store.dispatch('locations/getById', to.params.location_id)
        const restrictUser = restrictUserByAgencyPermission(location.permissions, company.id, to.name)
        if (restrictUser) {
          return next({
            name: 'dashboard',
            replace: true,
            params: { location_id: to.params.location_id },
          })
        }
        if (sidebarVersion != 'v2') {
          return next({
            name: 'dashboard',
            replace: true,
            params: { location_id: to.params.location_id },
          })
        }

        const restrictUserByScope = restrictUserByScopePermission(user.scopes, 'snapshots/api.readonly', to.name || '')
        if (restrictUserByScope) {
          return next({
            name: 'dashboard',
            replace: true,
            params: { location_id: to.params.location_id },
          })
        }

        if (
          (location?.suspended_info || location?.active_status_details?.is_temporarily_suspended) &&
          ![restrictedLocationRouteNames.default, suspendedLocationRouteNames.default, paymentSetupRouteNames.default].includes(to.name) &&
          user.type === User.TYPE_ACCOUNT
        ) {
          if (location?.suspended_info?.restricted && SUB_ACCOUNT_RESTRICTED_STATUS.includes(location?.suspended_info?.approval_state)) {
            return next({
              name: restrictedLocationRouteNames.default,
              replace: true,
              params: { location_id: to.params.location_id },
            })
          } else if (location?.suspended_info?.paused) {
            return next({
              name: suspendedLocationRouteNames.default,
              replace: true,
              params: { location_id: to.params.location_id },
            })
          } else if (location?.suspended_info?.payment_pending) {
            return next({
              name: paymentSetupRouteNames.default,
              replace: true,
              params: { location_id: to.params.location_id },
            })
          } else if (location?.active_status_details?.is_temporarily_suspended) {
            return next({
              name: suspendedLocationRouteNames.default,
              replace: true,
              params: { location_id: to.params.location_id },
            })
          }
        }

        if (firstLoad) {
          firstLoad = false
          await store.dispatch('auth/refreshFirebaseToken', { locationId: to.params.location_id || null, refresh: true }, { root: true })

          const locationId = to.params.location_id

          // Critical promises - must succeed for app to function
          const criticalBlockingOperations = executeCriticalPromises(
            [
              {
                name: 'locations/resetCurrentLocation',
                promise: store.dispatch('locations/resetCurrentLocation', { locationId }),
              },
              { name: 'users/syncAll', promise: store.dispatch('users/syncAll', locationId) },
              { name: 'featureFlags/syncAll', promise: store.dispatch('featureFlags/syncAll', locationId) },
            ],
            { context: 'Auth.requireAuthAndLocation' }
          )

          // Degradable promises - 90s timeout, can fail gracefully
          executeDegradablePromises(
            [
              { name: 'contacts/syncAll', promise: store.dispatch('contacts/syncAll', locationId) },
              {
                name: 'conversationAi/fetchConversationAIOptInForLocation',
                promise: store.dispatch('conversationAi/fetchConversationAIOptInForLocation', { entityId: locationId }),
              },
              // aiLocationPlan/fetchShowSummerOfAIMenu removed - Summer of AI feature is disabled (getter always returns false)
              { name: 'teams/syncAll', promise: store.dispatch('teams/syncAll', locationId) },
              { name: 'stripeConnect/syncAll', promise: store.dispatch('stripeConnect/syncAll', locationId) },
              { name: 'membership/syncAll', promise: store.dispatch('membership/syncAll', { locationId }) },
              { name: 'customObjectsStore/syncAll', promise: store.dispatch('customObjectsStore/syncAll', locationId) },
              {
                name: 'locationSetting/fetchAllProductOptInStatus',
                promise: store.dispatch('locationSetting/fetchAllProductOptInStatus'),
              },
            ],
            { context: 'Auth.requireAuthAndLocation.degradable' }
          )

          await criticalBlockingOperations

          // Fire-and-forget - no await, no timeout
          store.dispatch('filters/syncAll', locationId)
        }

        const locationLevelPermissionKey = locationLevelRoutesPermissions[to.name]
          ? locationLevelRoutesPermissions[to.name]
          : requiredPermission

        let locationHasPermission = true

        if (
          location.permissions &&
          location.settings?.saas_settings?.saas_mode == 'activated' &&
          location.settings?.saas_settings?.saas_plan_id
        ) {
          if (Array.isArray(locationLevelPermissionKey)) {
            const keyPermissions = locationLevelPermissionKey.filter(key => location.permissions[key] === true)
            // location has permission for any of the key in the list for route
            locationHasPermission = keyPermissions.length > 0
          } else {
            locationHasPermission = location.permissions[locationLevelPermissionKey] !== false
          }
        }

        if (!location || location.company_id !== company.id || location.deleted || !locationHasPermission) {
          if (
            location.permissions &&
            location.settings?.saas_settings?.saas_mode == 'activated' &&
            location.permissions &&
            location.settings?.saas_settings?.saas_plan_id &&
            !location.permissions[locationLevelRoutesPermissions.dashboard]
          ) {
            if (to.name === DashboardRouteNames.default) {
              const locationHasConversationPermission = location.permissions['conversations_enabled'] !== false
              if (locationHasConversationPermission) {
                return next({
                  name: conversationRouteNames.conversationsIdV2,
                  params: to.params,
                  replace: true,
                })
              } else {
                return next({
                  name: launchPadRouteNames.default,
                  params: to.params,
                  replace: true,
                })
              }
            } else {
              return next({
                path: from.path,
                replace: true,
              })
            }
          } else {
            return next({
              name: DashboardRouteNames.default,
              replace: true,
              params: { location_id: to.params.location_id },
            })
          }
        }
      }
      const allowNewPaymentsAccess = store.getters['company/isAbove97Plan']
      if (!allowNewPaymentsAccess && paymentBetaAccessRoutes.includes(to.name)) {
        return next({
          name: 'payment-integrations-v2',
          replace: true,
          params: { location_id: to.params.location_id },
        })
      }

      if (to.name === locationSettingsRouteNames.auditLogs && user.role !== 'admin') {
        return next({
          name: DashboardRouteNames.default,
          replace: true,
          params: { location_id: to.params.location_id },
        })
      }

      if (to.name === 'labs') {
        if (!(company.betaFeaturesEnabled && (user.type === 'agency' || (user.role === 'admin' && user.type !== 'agency')))) {
          return next({
            name: DashboardRouteNames.default,
            replace: true,
            params: { location_id: to.params.location_id },
          })
        }
      }

      if (
        (to.path?.includes('funnels-websites/funnels') ||
          to.path?.includes('funnels-websites/websites') ||
          to.path?.includes('funnels-websites/webinars')) &&
        to.params.location_id &&
        (to.params.funnel_id || to.query.folder)
      ) {
        try {
          const locationId = to.params.location_id
          const id = to.query.folder ?? to.params.funnel_id
          const {
            data: { data },
          } = await FunnelsServices.getById(id)

          if (!data || data.locationId !== locationId) {
            const redirectToAgencyLaunchpad = await store.dispatch('launchpad/redirectToAgencyLaunchpad')
            if (redirectToAgencyLaunchpad) {
              return next({ name: agencyLaunchpadRoutes.default, replace: true })
            } else {
              return next({ name: agencyDashboardRoutes.main, replace: true })
            }
          } else {
            return next()
          }
        } catch (error) {
          console.error(error)
          const redirectToAgencyLaunchpad = await store.dispatch('launchpad/redirectToAgencyLaunchpad')
          if (redirectToAgencyLaunchpad) {
            return next({ name: agencyLaunchpadRoutes.default, replace: true })
          } else {
            return next({ name: agencyDashboardRoutes.main, replace: true })
          }
        }
      }
      await setupLocale(to, from)
      next()
    })
    .catch(() => {
      next({
        name: 'login',
        query: { url: encodeURIComponent(to.fullPath) },
        replace: true,
      })
    })
}

export const _requireAccessLevelAndLocation = (to: Route, from: Route, next: (to?: RawLocation) => void) => {
  store
    .dispatch('auth/get')
    .then(async () => {
      const currentUser = await store.dispatch('user/get')
      const user = new User(cloneDeep(currentUser))
      const company = await store.dispatch('company/get')
      if (CUSTOM_AGENCIES_PERMISSIONS[company?.id] && user.type === User.TYPE_ACCOUNT) {
        const locationId = to.params.location_id
        const location = await store.dispatch('locations/getById', locationId)
        const restrictUser = restrictUserByAgencyPermission(location.permissions, company.id, to.name)
        if (restrictUser) {
          return next({
            name: 'dashboard',
            replace: true,
            params: { location_id: locationId },
          })
        }
      }
      if (user.type === User.TYPE_ACCOUNT && user.role === User.ROLE_USER) {
        const locationId = first(keys(user.locations || {}).sort()) || ''
        const sidebarVersion = store.getters['sidebarv2/getVersion']
        if (sidebarVersion != 'v2') {
          return next({
            name: 'dashboard',
            replace: true,
            params: { location_id: locationId },
          })
        } else {
          return next({
            name: DashboardRouteNames.default,
            replace: true,
            params: { location_id: locationId },
          })
        }
      } else {
        next()
      }
    })
    .catch(() => {
      next({
        name: 'login',
        query: { url: encodeURIComponent(to.fullPath) },
        replace: true,
      })
    })
}

export function requireAuthAndScope(scope: string) {
  return async (to: Route, from: Route, next: any) => {
    try {
      const user = new User(await store.dispatch('user/get'))
      if (user.scopes.includes(scope)) {
        await requireAuth(to, from, next)
      } else {
        next({ path: '/', replace: true })
      }
    } catch {
      next({ path: '/', replace: true })
    }
  }
}

// handles redirection logic for agency launchpad settings page, it is visible only for older agencies
export function isAgencyLaunchpadSettingsEnabled() {
  return async (to: Route, from: Route, next: any) => {
    const company = await store.dispatch('company/get')

    let isEnabled = false

    if (company && company.dateAdded) {
      // Hide if company.dateAdded is greater than September 3rd, 2025
      const cutoffDate = moment('2025-09-03')
      const companyDate = moment(company.dateAdded)
      isEnabled = companyDate.isSameOrBefore(cutoffDate)
    }

    if (isEnabled) {
      next()
    } else {
      next({ path: '/', replace: true })
    }
  }
}
